import type { PlannerProject, WardrobeModule } from "../types";
import { usableItemPieces } from "./compartments";
import { decorLabel, resolveDecor } from "./decors";
import { eggerDecor } from "./eggerDecors";

/**
 * Полки и ящики шкафа в своём декоре (приёмка 16.10): «как корпус» или свой
 * декор Egger, один на весь шкаф. Ящики — ещё «в цвет фасадов», если фасады
 * ЛДСП или МДФ в эмали. У ящика меняется лицевая панель, короб остаётся.
 */

export const supportsInteriorDecor = (module: Pick<WardrobeModule, "type">) =>
  module.type === "single" || module.type === "double" || module.type === "mezzanine" || module.type === "corner45";

/** Популярные декоры — быстрый выбор без каталога (как у задника). */
export const INTERIOR_DECOR_SUGGESTIONS = ["W1000 ST9", "H1145 ST10", "U999 ST19", "H1714 ST19"] as const;

export const normalizeShelfDecor = (module: Pick<WardrobeModule, "type" | "shelfDecor">) =>
  supportsInteriorDecor(module) ? eggerDecor(module.shelfDecor)?.code : undefined;

/** Ящики в цвет фасадов — только при фасадах ЛДСП или эмали. */
export const drawerFrontsCanFollowFacade = (module: Pick<WardrobeModule, "facade">) =>
  module.facade === "wood" || module.facade === "satin" || module.facade === "gloss";

export const normalizeDrawerDecor = (module: Pick<WardrobeModule, "type" | "drawerDecor" | "facade">) => {
  if (!supportsInteriorDecor(module)) return undefined;
  if (module.drawerDecor === "facade") return drawerFrontsCanFollowFacade(module) ? "facade" as const : undefined;
  return eggerDecor(module.drawerDecor)?.code;
};

type DecorSource = Pick<PlannerProject, "carcassDecor" | "carcassMaterial">;

/** Декор полок: свой или корпуса. */
export const shelfDecorOf = (project: DecorSource, module: WardrobeModule) =>
  resolveDecor(normalizeShelfDecor(module) ?? module.decor ?? project.carcassDecor, module.material ?? project.carcassMaterial);

/**
 * Лицевая панель внутреннего ящика: в декоре корпуса, в своём декоре или в
 * отделке фасадов (декор фасада ЛДСП или эмаль того же цвета).
 */
export type DrawerFrontFinish =
  | { kind: "carcass" }
  | { kind: "decor"; code: string }
  | { kind: "facade" };

export const drawerFrontFinish = (module: WardrobeModule): DrawerFrontFinish => {
  const decor = normalizeDrawerDecor(module);
  if (decor === "facade") return { kind: "facade" };
  if (decor) return { kind: "decor", code: decor };
  return { kind: "carcass" };
};

export const hasOwnShelfDecor = (module: WardrobeModule) => Boolean(normalizeShelfDecor(module));
export const hasOwnDrawerDecor = (module: WardrobeModule) => drawerFrontFinish(module).kind !== "carcass";

/** Подпись для техкарты и сметы клиенту. */
export const shelfDecorDescription = (project: DecorSource, module: WardrobeModule) =>
  hasOwnShelfDecor(module)
    ? `${decorLabel(shelfDecorOf(project, module))} · свой декор полок`
    : `в декоре корпуса (${decorLabel(shelfDecorOf(project, module))})`;

export const drawerDecorDescription = (project: DecorSource, module: WardrobeModule, facadeText: () => string) => {
  const finish = drawerFrontFinish(module);
  if (finish.kind === "facade") return `в отделке фасадов (${facadeText()})`;
  if (finish.kind === "decor") return `${decorLabel(resolveDecor(finish.code))} · свой декор ящиков`;
  return `в декоре корпуса (${decorLabel(resolveDecor(module.decor ?? project.carcassDecor, module.material ?? project.carcassMaterial))})`;
};

/** Полки шкафа (обычные, с подсветкой, обувница) по отсекам, м²: как в 3D, глубина корпуса − 36 мм. */
export const interiorShelfAreaM2 = (module: WardrobeModule) => {
  if (module.type === "corner45") return 0;
  const depth = Math.max(0, module.depth - 36);
  return module.items
    .filter((item) => item.type === "shelf" || item.type === "led" || item.type === "shoeRack")
    .reduce((sum, item) => sum + usableItemPieces(module, item).reduce((total, piece) => total + piece.width * depth, 0), 0) / 1_000_000;
};

/**
 * Лицевые панели внутренних ящиков, м²: по отсекам, за распашными дверями —
 * на проставках по 20 мм, как в 3D. У углового — ящики корпуса Б.
 */
export const interiorDrawerFrontsAreaM2 = (module: WardrobeModule) => {
  // В угловом ящиков нет — только полки и штанги (28.10).
  if (module.type === "corner45") return 0;
  const spacers = module.door === "hinged" ? 40 : 0;
  return module.items
    .filter((item) => item.type === "drawer")
    .reduce((sum, item) => sum + usableItemPieces(module, item)
      .reduce((total, piece) => total + Math.max(0, piece.width - spacers - 6) * (item.height ?? 180), 0), 0) / 1_000_000;
};
